// Period koji faktura firmi stvarno obuhvata. Čist modul (bez importa), koriste
// ga PDF, admin i supervizorski prikaz.
//
// `periodStart`/`periodEnd` su poluotvoren interval mjeseca (ili privremene
// fakture), ali stavke nemaju donju granicu: zakašnjela seansa iz ranijeg
// mjeseca ulazi u prvu sljedeću fakturu. Natpis "Period" zato kreće od
// najranije stavke kad je ona ranija od `periodStart`.
import type { CompanyInvoiceSummary } from "@/types/company-billing";

export type InvoicePeriodInput = Pick<CompanyInvoiceSummary, "kind" | "periodStart" | "periodEnd" | "issuedAt"> & {
  coveredStart?: string | null;
};

/** Raniji od dva ISO trenutka (null se ignoriše). */
export function earlierIso(a: string, b: string | null | undefined): string {
  if (!b) return a;
  return Date.parse(b) < Date.parse(a) ? b : a;
}

/**
 * Prvi i zadnji obuhvaćeni trenutak, oba uključivo. Kraj je poluotvoren pa je
 * zadnji uključeni trenutak milisekunda prije `periodEnd`. Faktura za dopunu
 * kredita nema period, oba kraja su datum izdavanja.
 */
export function invoiceCoveredRange(inv: InvoicePeriodInput): { from: string; to: string } {
  if (inv.kind === "credit_topup") {
    const at = inv.issuedAt ?? inv.periodStart;
    return { from: at, to: at };
  }
  const to = new Date(Math.max(Date.parse(inv.periodStart), Date.parse(inv.periodEnd) - 1)).toISOString();
  return { from: earlierIso(inv.periodStart, inv.coveredStart), to };
}

/**
 * Natpis perioda: "od - do", ili jedan datum kad su oba u istom danu (npr.
 * privremena faktura napravljena prvog u mjesecu bez ranijih stavki).
 * `fmt` formatira ISO trenutak kao lokalni datum.
 */
export function formatCoveredRange(inv: InvoicePeriodInput, fmt: (iso: string) => string): string {
  const { from, to } = invoiceCoveredRange(inv);
  const a = fmt(from);
  const b = fmt(to);
  return a === b ? a : `${a} - ${b}`;
}
